import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { FileText, Video, File as FileIcon, Upload, X, Eye } from 'lucide-react';
import { Progress } from '../../../core/progress';
import { useMediaUpload } from '../../../hooks/use-media-upload';
import { WorkbookPanel, ZineButton } from '../../zine';
import { zineColors } from '../../zine/zine-theme';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  WorkbookVerdict,
  workbookCardStyle,
} from './zine-workbook';
import { FilePreviewDialog } from '../shared/file-preview-dialog';

export const SUBMISSION_ACCEPTED_TYPES = [
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const SUBMISSION_MAX_SIZE = 500 * 1024 * 1024; // 500MB

interface UploadedFile {
  url: string;
  filename: string;
  contentType: string;
  fileSize: number;
}

interface FileUploadExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

function getFileTypeIcon(contentType: string) {
  if (contentType === 'application/pdf') {
    return <FileText className="h-5 w-5" style={{ color: zineColors.redApplied }} />;
  }
  if (contentType?.startsWith('video/')) {
    return <Video className="h-5 w-5" style={{ color: zineColors.sizzle }} />;
  }
  if (contentType?.includes('presentation') || contentType?.includes('powerpoint')) {
    return <FileText className="h-5 w-5" style={{ color: zineColors.mid }} />;
  }
  return <FileIcon className="h-5 w-5" style={{ color: zineColors.warmGray }} />;
}

function formatFileSize(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: FileUploadExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const initialFiles = useMemo<UploadedFile[]>(() => {
    const submission = content.lastSubmission;
    if (
      submission &&
      typeof submission === 'object' &&
      'type' in submission &&
      submission.type === ExerciseType.FileUpload &&
      'files' in submission &&
      Array.isArray((submission as any).files)
    ) {
      return (submission as any).files as UploadedFile[];
    }
    return [];
  }, [content.lastSubmission]);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(initialFiles);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadState, uploadMedia, resetUpload } = useMediaUpload();

  // Reset files when content changes (navigating between exercises)
  useEffect(() => {
    setUploadedFiles(initialFiles);
  }, [content.id, initialFiles]);

  const handleUploadFile = useCallback(async (file: File) => {
    // Validate file type
    if (!SUBMISSION_ACCEPTED_TYPES.includes(file.type)) {
      console.error(`File type not supported: ${file.type}`);
      return;
    }
    // Validate file size
    if (file.size > SUBMISSION_MAX_SIZE) {
      console.error(`File too large: ${file.size} bytes (max ${SUBMISSION_MAX_SIZE})`);
      return;
    }

    try {
      const result = await uploadMedia(file);
      if (result && result.mediaUrl) {
        setUploadedFiles((prev) => [
          ...prev,
          {
            url: result.mediaUrl,
            filename: file.name,
            contentType: file.type,
            fileSize: file.size,
          },
        ]);
        resetUpload();
      }
    } catch (err) {
      console.error('File upload failed:', err);
    }
  }, [uploadMedia, resetUpload]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      for (let i = 0; i < files.length; i++) {
        handleUploadFile(files[i]);
      }
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleUploadFile]
  );

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const files = e.dataTransfer.files;
      if (!files) return;
      for (let i = 0; i < files.length; i++) {
        handleUploadFile(files[i]);
      }
    },
    [handleUploadFile]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.FileUpload as any,
        files: uploadedFiles,
      } as any);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUploading =
    uploadState.status === 'requesting-urls' ||
    uploadState.status === 'uploading' ||
    uploadState.status === 'composing';

  const acceptString = SUBMISSION_ACCEPTED_TYPES.join(',');

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);

  const renderFileRow = (
    file: UploadedFile,
    idx: number,
    action: React.ReactNode,
  ) => (
    <div
      key={file.url ?? idx}
      className="flex items-center gap-3 p-3"
      style={workbookCardStyle}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[4px]"
        style={{ background: zineColors.cream, border: `1px solid ${zineColors.line}` }}
      >
        {getFileTypeIcon(file.contentType)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-sm font-medium" style={{ color: zineColors.ink }}>
          {file.filename ?? 'Unknown file'}
        </p>
        <p className="m-0 text-xs" style={{ color: zineColors.warmGray }}>
          {formatFileSize(file.fileSize)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setPreviewFile(file)}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition hover:opacity-80"
        style={{
          background: zineColors.ink,
          borderColor: zineColors.ink,
          color: '#fff',
        }}
        aria-label={t('zine.workbook.viewFile', { name: file.filename })}
      >
        <Eye className="h-3.5 w-3.5" />
        {t('zine.workbook.view')}
      </button>
      {action}
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.upload')}
        title={
          content.title
            ? renderInlineMarkdown(content.title, 'file-upload-exercise-title')
            : undefined
        }
        hint={t('zine.workbook.uploadPrompt')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton
                type="submit"
                isLoading={isSubmitting}
                disabled={uploadedFiles.length === 0 || isSubmitting || isUploading}
              >
                {isSubmitting ? t('zine.workbook.submitting') : t('zine.workbook.submitFiles')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />

        {isReadOnly ? (
          /* Read-only mode: show file list with download links */
          <div className="mb-4">
            {uploadedFiles.length > 0 ? (
              <div className="space-y-2">
                {uploadedFiles.map((file, idx) =>
                  renderFileRow(
                    file,
                    idx,
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold transition hover:opacity-80"
                      style={{ borderColor: zineColors.line, color: zineColors.ink }}
                    >
                      {t('zine.workbook.download')}
                    </a>,
                  ),
                )}
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-8 text-center"
                style={workbookCardStyle}
              >
                <div
                  className="mb-3 flex h-16 w-16 items-center justify-center rounded-full"
                  style={{ background: zineColors.baby }}
                >
                  <Upload className="h-8 w-8" style={{ color: zineColors.charm }} />
                </div>
                <p className="m-0 text-sm" style={{ color: zineColors.warmGray }}>
                  {t('zine.workbook.noFiles')}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Edit mode: upload zone + file list */
          <div className="mb-4 space-y-3">
            {/* Upload zone */}
            <div
              className={cn(
                'cursor-pointer rounded-[4px] border-2 border-dashed p-6 text-center transition-colors',
              )}
              style={{
                background: dragActive ? 'rgba(248,215,221,.45)' : '#fff',
                borderColor: dragActive ? zineColors.redApplied : zineColors.line,
              }}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                accept={acceptString}
                className="hidden"
              />
              <Upload className="mx-auto mb-3 h-10 w-10" style={{ color: zineColors.charm }} />
              <p className="m-0 text-sm font-medium" style={{ color: zineColors.ink }}>
                {t('zine.workbook.uploadDropHint')}
              </p>
              <p className="m-0 mt-1 text-xs" style={{ color: zineColors.warmGray }}>
                {t('zine.workbook.uploadTypes')}
              </p>
            </div>

            {/* Upload progress */}
            {isUploading && (
              <div className="space-y-2 p-4" style={workbookCardStyle}>
                <Progress value={uploadState.progress} className="w-full" />
                <p className="m-0 text-center text-sm" style={{ color: zineColors.warmGray }}>
                  {uploadState.status === 'requesting-urls' && t('zine.workbook.uploadPreparing')}
                  {uploadState.status === 'uploading' &&
                    t('zine.workbook.uploadProgress', { percent: uploadState.progress ?? 0 })}
                  {uploadState.status === 'composing' && t('zine.workbook.uploadFinalizing')}
                </p>
              </div>
            )}

            {/* Upload error */}
            {uploadState.status === 'error' && uploadState.error && (
              <div
                className="rounded-[4px] border p-3 text-sm font-medium"
                style={{
                  background: zineColors.baby,
                  borderColor: zineColors.redApplied,
                  color: zineColors.redApplied,
                }}
              >
                {uploadState.error}
              </div>
            )}

            {/* Uploaded files list */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                {uploadedFiles.map((file, idx) =>
                  renderFileRow(
                    file,
                    idx,
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(idx)}
                      className="shrink-0 rounded-full p-1.5 transition hover:opacity-70"
                      style={{ color: zineColors.warmGray }}
                      aria-label={t('zine.workbook.removeFile', { name: file.filename })}
                    >
                      <X className="h-4 w-4" />
                    </button>,
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </WorkbookPanel>
      <FilePreviewDialog
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        downloadLabel={t('zine.workbook.download')}
        unavailableMessage={t('zine.workbook.previewUnavailable')}
      />
    </form>
  );
}
